import { useMemo, useState } from "react";
import { Repeat } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, StatCard, InfoRow, compactINR } from "@/components/tools/kit";
import { CompareLines } from "@/components/tools/charts";
import { ToolAdvisor } from "@/components/tools/ToolAdvisor";
import { buyVsRent } from "@/lib/calculators";

export default function BuyVsRent() {
  const [propertyPrice, setPrice] = useState(12000000);
  const [downPayment, setDown] = useState(2400000);
  const [loanRate, setLoanRate] = useState(8.5);
  const [loanYears, setLoanYears] = useState(20);
  const [appreciation, setApp] = useState(6);
  const [monthlyRent, setRent] = useState(30000);
  const [rentInflation, setRentInf] = useState(7);
  const [investReturn, setInvest] = useState(11);
  const [horizon, setHorizon] = useState(20);
  const r = useMemo(() => buyVsRent({ propertyPrice, downPayment, loanRate, loanYears, appreciation, monthlyRent, rentInflation, investReturn, horizon }),
    [propertyPrice, downPayment, loanRate, loanYears, appreciation, monthlyRent, rentInflation, investReturn, horizon]);
  const verdictBuy = r.verdict === "buy";
  return (
    <ToolLayout title="Buy vs Rent" subtitle="Which path builds more wealth over your horizon?" icon={Repeat} accent="#2D6A4F">
      <TwoCol
        left={
          <SectionCard title="Assumptions">
            <div className="space-y-5">
              <Field label="Property price"><MoneyInput testid="bvr-price" value={propertyPrice} onChange={setPrice} helper={compactINR(propertyPrice)} /></Field>
              <Field label="Down payment"><MoneyInput testid="bvr-down" value={downPayment} onChange={setDown} helper={compactINR(downPayment)} /></Field>
              <Field label="Loan rate (p.a.)" hint={loanRate + "%"}><SliderRow testid="bvr-loan-rate" value={loanRate} min={6} max={14} step={0.05} suffix="%" onChange={setLoanRate} /></Field>
              <Field label="Loan tenure" hint={loanYears + " yrs"}><SliderRow testid="bvr-loan-years" value={loanYears} min={5} max={30} suffix=" yr" onChange={setLoanYears} /></Field>
              <Field label="Property appreciation (p.a.)" hint={appreciation + "%"}><SliderRow testid="bvr-appreciation" value={appreciation} min={0} max={12} step={0.5} suffix="%" onChange={setApp} /></Field>
              <Field label="Monthly rent (equivalent home)"><MoneyInput testid="bvr-rent" value={monthlyRent} onChange={setRent} helper={compactINR(monthlyRent) + " / month"} /></Field>
              <Field label="Rent inflation (p.a.)" hint={rentInflation + "%"}><SliderRow testid="bvr-rent-inflation" value={rentInflation} min={0} max={12} step={0.5} suffix="%" onChange={setRentInf} /></Field>
              <Field label="Investment return on savings (p.a.)" hint={investReturn + "%"}><SliderRow testid="bvr-invest" value={investReturn} min={4} max={18} step={0.5} suffix="%" onChange={setInvest} /></Field>
              <Field label="Horizon" hint={horizon + " yrs"}><SliderRow testid="bvr-horizon" value={horizon} min={5} max={40} suffix=" yr" onChange={setHorizon} /></Field>
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className={`rounded-2xl p-5 text-white shadow-sm ${verdictBuy ? "bg-[#2D6A4F]" : "bg-[#D96B43]"}`} data-testid="bvr-verdict">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70">After {horizon} years</div>
              <div className="font-heading text-2xl sm:text-3xl font-extrabold mt-1">
                {verdictBuy ? "Buying" : "Renting & investing"} builds more wealth
              </div>
              <div className="text-sm text-white/80 mt-1">
                Difference of {compactINR(Math.abs(r.buyerNet - r.renterNet))} in net worth
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard testid="bvr-emi" label="Home Loan EMI" value={compactINR(r.emi)} accent="#D99B26" />
              <StatCard testid="bvr-buyer" label="Buyer Net Worth" value={compactINR(r.buyerNet)} accent="#2D6A4F" />
              <StatCard testid="bvr-renter" label="Renter Net Worth" value={compactINR(r.renterNet)} accent="#2A6F7F" />
            </div>
            <SectionCard title="Net worth over time">
              <CompareLines data={r.points} keys={[{ key: "buyer", name: "Buyer", color: "#2D6A4F" }, { key: "renter", name: "Renter + Invest", color: "#D96B43" }]} />
            </SectionCard>
            <ToolAdvisor tool="buy-vs-rent" title="Buy vs Rent"
              context={[
                { label: "Property price", value: compactINR(propertyPrice) },
                { label: "Down payment", value: compactINR(downPayment) },
                { label: "Monthly rent", value: compactINR(monthlyRent) },
                { label: "Appreciation", value: `${appreciation}%` },
                { label: "Investment return", value: `${investReturn}%` },
                { label: "Horizon", value: `${horizon} yrs` },
                { label: "Buyer net worth", value: compactINR(r.buyerNet) },
                { label: "Renter net worth", value: compactINR(r.renterNet) },
              ]} />
          </>
        }
      />
    </ToolLayout>
  );
}
